import moment from 'moment'
import { fromUTCMidnightSeconds } from 'utils/time'
import { DEPLOYMENT_KEYS } from 'utils/constants'
import {
  ANCHOR_TYPE,
  COMPLETED_BY,
  FIXED_DATE_MODE,
  INSTRUMENT_EVENT,
  OFFSET_DIRECTION,
  OFFSET_UNIT,
  VISIT_STATUS,
} from '../constants'
import { createEmptySchedule } from '../models/createEmptySchedule'
import { createEmptyAnchor } from '../models/createEmptyAnchor'
import { createLocalId } from '../models/ids'
import { mapCohortToTargeting } from './fromApiSchedule'
import {
  mapLegacyAvailability,
  mapLegacyEndRules,
  mapLegacyRepeat,
  mapPlatformFromLegacy,
} from './legacySchedulePayload'

export const isV3ScheduleEntry = entry => Array.isArray(entry?.deploy?.anchors)

const intervalToDays = (interval = 0, intervalType = 'days') => {
  const value = Number(interval) || 0
  return intervalType === 'weeks' ? value * 7 : value
}

const mapLegacyDeployToAnchor = (legacy = {}) => {
  const deploy = legacy.deploy || {}
  const chain = legacy.chain_deployment_info || {}
  const anchor = createEmptyAnchor({ id: createLocalId('anchor') })

  if (DEPLOYMENT_KEYS.firstLogin in deploy) {
    return {
      ...anchor,
      type: ANCHOR_TYPE.participantLogin,
      offset: {
        amount: intervalToDays(deploy.first_login.interval, deploy.first_login.interval_type),
        unit: OFFSET_UNIT.days,
        direction: OFFSET_DIRECTION.after,
      },
    }
  }

  if (DEPLOYMENT_KEYS.abs in deploy) {
    const date = fromUTCMidnightSeconds(deploy.absolute)
    return {
      ...anchor,
      type: ANCHOR_TYPE.fixedCalendarDate,
      fixedDate: {
        mode: FIXED_DATE_MODE.day,
        value: date.isValid() ? date.format('YYYY-MM-DD') : '',
      },
      offset: { amount: 0, unit: OFFSET_UNIT.days, direction: OFFSET_DIRECTION.after },
    }
  }

  if (DEPLOYMENT_KEYS.chainDeployByVisit in chain) {
    const visitIds = chain[DEPLOYMENT_KEYS.visitIds] || chain[DEPLOYMENT_KEYS.visitId] || []
    const ids = Array.isArray(visitIds) ? visitIds : [visitIds].filter(Boolean)
    const visitStatus = legacy.article_type === 'missed_visit_follow_up'
      ? VISIT_STATUS.missedDate
      : VISIT_STATUS.completedDate
    return {
      ...anchor,
      type: ANCHOR_TYPE.visit,
      visitStatus,
      instanceIds: ids.map(String),
      visitRefs: ids.map(visitId => ({ visitId: String(visitId), armId: '', label: '' })),
      offset: {
        amount: Math.abs(Number(deploy[DEPLOYMENT_KEYS.daysOffset] || 0)),
        unit: OFFSET_UNIT.days,
        direction: Number(deploy[DEPLOYMENT_KEYS.daysOffset] || 0) < 0 ? OFFSET_DIRECTION.before : OFFSET_DIRECTION.after,
      },
    }
  }

  if (DEPLOYMENT_KEYS.chainDeployOffset in deploy || chain.prev_instrument_id) {
    const instrumentIds = chain.prev_instrument_id
    const instanceId = Array.isArray(instrumentIds) ? instrumentIds[0] : instrumentIds
    return {
      ...anchor,
      type: ANCHOR_TYPE.instrument,
      instanceId: instanceId ? String(instanceId) : '',
      instrumentEvent: INSTRUMENT_EVENT.completion,
      offset: {
        amount: Number(deploy[DEPLOYMENT_KEYS.chainDeployOffset] || 0),
        unit: OFFSET_UNIT.days,
        direction: OFFSET_DIRECTION.after,
      },
    }
  }

  // deploy.now and unknown legacy shapes default to participant start.
  return {
    ...anchor,
    type: ANCHOR_TYPE.participantStartDate,
    offset: { amount: 0, unit: OFFSET_UNIT.days, direction: OFFSET_DIRECTION.after },
  }
}

/** Convert legacy metadata.schedule object (V1/V2) into a scheduling-engine draft schedule. */
export const fromLegacyV2Schedule = legacyEntry => {
  const base = createEmptySchedule()
  const cohortSource = legacyEntry?.audience?.cohort || legacyEntry?.cohort
  const targeting = mapCohortToTargeting(cohortSource)
  const endRules = mapLegacyEndRules(legacyEntry, base.endRules)
  const availability = mapLegacyAvailability(legacyEntry, base.availability)

  return {
    ...base,
    id: createLocalId('schedule'),
    scheduleId: legacyEntry?.schedule_id || legacyEntry?.scheduleId || base.scheduleId,
    version: legacyEntry?.version || base.version,
    name: legacyEntry?.name || '',
    label: legacyEntry?.name || legacyEntry?.label || '',
    anchors: [mapLegacyDeployToAnchor(legacyEntry)],
    repeat: mapLegacyRepeat(legacyEntry, base.repeat),
    endRules,
    availability,
    timeOfDay: legacyEntry?.deploy?.time_of_day || base.timeOfDay,
    audience: {
      completedBy: legacyEntry?.audience?.completed_by || COMPLETED_BY.subject,
      platformType: mapPlatformFromLegacy(legacyEntry?.platform_type || legacyEntry?.audience?.platform_type),
      inheritedFromAnchor: { arm: false, phase: false },
    },
    instancePermissions: base.instancePermissions,
    targeting,
    visibility: legacyEntry?.visibility,
    visibilityRanges: legacyEntry?.visibilityRanges,
  }
}

export const fromLegacyV2Schedules = payload => {
  const entries = Array.isArray(payload) ? payload.filter(Boolean) : payload ? [payload] : []
  const schedules = entries.map(entry =>
    isV3ScheduleEntry(entry) ? null : fromLegacyV2Schedule(entry),
  ).filter(Boolean)
  return { schedules: schedules.length ? schedules : [createEmptySchedule()] }
}
